import { getMerchantGeneralSettings, putMerchantGeneralSettings } from 'APIs/merchants/merchants';
import { MerchantVelocityConfig } from 'APIs/merchants/merchants.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { H2, Input, Label, LpBox, LpDetails, Row } from 'Constants/styles';
import { ChangeEvent, FormEvent, MouseEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import getErrorMessage from 'utils/getErrorMessage';

import { EditBtn, EditFormRow, RowP } from './styles';

const initLimits = {
  limitPerSecond: 0,
  limitPerMinute: 0,
  limitPerHour: 0,
};
const initValues: MerchantVelocityConfig = {
  limits_order_marketplaceId: initLimits,
  limits_order_internalOrderId: initLimits,
  limits_order_paymentAmount: initLimits,
  limits_order_billing_name: initLimits,
  limits_order_billing_address: initLimits,
  limits_order_billing_items: initLimits,
  limits_order_ipAddress: initLimits,
  limits_order_checkoutCorrelationId: initLimits,
  limits_payto_resolveAlias: initLimits,
  limits_payto_sendAgreement: initLimits,
  limits_payto_getAgreementStatus: initLimits,
};

type Props = {
  merchantId: string;
  initMerchantVelocityConfig?: MerchantVelocityConfig;
};

const VelocityLimits = ({ merchantId, initMerchantVelocityConfig }: Props): ReactElement => {
  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [modalVelocityConfig, setModalVelocityConfig] = useState<MerchantVelocityConfig>(initValues);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const [merchantVelocityConfig, setMerchantVelocityConfig] = useState<MerchantVelocityConfig>(initValues);

  const handleCancel = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(false);
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!apiAprilBaseUri || !merchantId) {
        return;
      }

      try {
        setModalIsLoading(true);
        const resp = await getMerchantGeneralSettings(apiAprilBaseUri, merchantId);
        await putMerchantGeneralSettings(apiAprilBaseUri, merchantId, { ...resp, velocityConfig: modalVelocityConfig });
        setMerchantVelocityConfig(modalVelocityConfig);
        setShowModal(false);
      } catch (e) {
        setModalErrorMsg(getErrorMessage(e));
      } finally {
        setModalIsLoading(false);
      }
    },
    [apiAprilBaseUri, merchantId, modalVelocityConfig],
  );

  const toggleModal = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(true);
    if (merchantVelocityConfig) setModalVelocityConfig(merchantVelocityConfig);
    else setModalVelocityConfig(initValues);
    setModalIsLoading(false);
    setModalErrorMsg('');
  };

  const handleCloseModalMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setModalErrorMsg('');
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { type, name, value } = e.target;

    if (type === 'text') {
      const result = value.length > 0 ? Number(value) : null;
      const keys = name.split('.');

      setModalVelocityConfig((prev) => ({
        ...prev,
        [keys[0]]: { ...prev[keys[0] as keyof MerchantVelocityConfig], [keys[1]]: result },
      }));
    }
  };

  useEffect(() => {
    if (initMerchantVelocityConfig) {
      setMerchantVelocityConfig(initMerchantVelocityConfig);
    }
  }, [initMerchantVelocityConfig]);

  return (
    <LpBox>
      <Row>
        <div className="col-sm-7">
          <H2 className="mb-0">Velocity limits</H2>
        </div>
        <div className="col-sm-5 text-right">
          <EditBtn onClick={toggleModal} data-testid="edit-velocity-limits-btn">
            Edit velocity limits
          </EditBtn>
        </div>
      </Row>
      <RowP data-testid="velocity-limits-table">
        Rate at which API requests can be made within a specific timeframe. It is used to control the number of requests
        a client can send to an API over a given period, such as per second, minute, or hour. The goal is to prevent
        abuse, ensure fair usage, protect server resources, and maintain API performance and availability.
      </RowP>
      <LpDetails data-testid="velocity-limits-table">
        <Row className="pt-4">
          <div className="col-sm-3"></div>
          <div className="col-sm-3">Per second</div>
          <div className="col-sm-3">Per minute</div>
          <div className="col-sm-3">Per hour</div>
        </Row>
        <Row className="pt-4"></Row>
        <Row className="pt-4" data-testid="limits-order-marketplace-id">
          <div className="col-sm-3">
            <Label>Order marketplace Id</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_marketplaceId.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_marketplaceId.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_marketplaceId.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-internal-id">
          <div className="col-sm-3">
            <Label>Order internal Id</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_internalOrderId.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_internalOrderId.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_internalOrderId.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-payment-amount">
          <div className="col-sm-3">
            <Label>Order payment amount</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_paymentAmount.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_paymentAmount.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_paymentAmount.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-billing-name">
          <div className="col-sm-3">
            <Label>Order billing name</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_name.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_name.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_name.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-billing-address">
          <div className="col-sm-3">
            <Label>Order billing address</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_address.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_address.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_address.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-billing-items">
          <div className="col-sm-3">
            <Label>Order billing items</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_items.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_items.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_billing_items.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-ip-address">
          <div className="col-sm-3">
            <Label>Order IP address</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_ipAddress.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_ipAddress.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_order_ipAddress.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-order-checkout-correlation-id">
          <div className="col-sm-3">
            <Label>Order checkout correlation Id</Label>
          </div>
          <div className="col-sm-3">
            {merchantVelocityConfig?.limits_order_checkoutCorrelationId.limitPerSecond ?? '-'}
          </div>
          <div className="col-sm-3">
            {merchantVelocityConfig?.limits_order_checkoutCorrelationId.limitPerMinute ?? '-'}
          </div>
          <div className="col-sm-3">
            {merchantVelocityConfig?.limits_order_checkoutCorrelationId.limitPerHour ?? '-'}
          </div>
        </Row>
        <Row className="pt-4" data-testid="limits-payto-resolve-alias">
          <div className="col-sm-3">
            <Label>PayTo resolve alias</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_resolveAlias?.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_resolveAlias?.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_resolveAlias?.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-payto-send-agreement">
          <div className="col-sm-3">
            <Label>PayTo send agreement</Label>
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_sendAgreement?.limitPerSecond ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_sendAgreement?.limitPerMinute ?? '-'}</div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_sendAgreement?.limitPerHour ?? '-'}</div>
        </Row>
        <Row className="pt-4" data-testid="limits-payto-get-agreement-status">
          <div className="col-sm-3">
            <Label>PayTo get agreement status</Label>
          </div>
          <div className="col-sm-3">
            {merchantVelocityConfig?.limits_payto_getAgreementStatus?.limitPerSecond ?? '-'}
          </div>
          <div className="col-sm-3">
            {merchantVelocityConfig?.limits_payto_getAgreementStatus?.limitPerMinute ?? '-'}
          </div>
          <div className="col-sm-3">{merchantVelocityConfig?.limits_payto_getAgreementStatus?.limitPerHour ?? '-'}</div>
        </Row>
      </LpDetails>
      {showModal && (
        <Modal
          title="Edit velocity limits"
          cancelBtnText="Cancel"
          confirmBtnText="Save changes"
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          disableConfirmBtn={modalIsLoading}
          isLoading={modalIsLoading}
          large={true}
          testId={'velocity-limits-modal'}
        >
          {modalErrorMsg.length > 0 && (
            <Message
              className="mt-2 mb-0"
              success={false}
              description={modalErrorMsg}
              handleClose={handleCloseModalMsg}
            />
          )}
          <LpDetails className="pt-2 pb-0">
            <EditFormRow className="mt-2">
              <div className="col-sm-3"></div>
              <div className="col-sm-3">Per second</div>
              <div className="col-sm-3">Per minute</div>
              <div className="col-sm-3">Per hour</div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order marketplace Id</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_marketplaceId.limitPerSecond"
                  value={modalVelocityConfig.limits_order_marketplaceId.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-marketplace-id-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_marketplaceId.limitPerMinute"
                  value={modalVelocityConfig.limits_order_marketplaceId.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-marketplace-id-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_marketplaceId.limitPerHour"
                  value={modalVelocityConfig.limits_order_marketplaceId.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-marketplace-id-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order internal order Id</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_internalOrderId.limitPerSecond"
                  value={modalVelocityConfig.limits_order_internalOrderId.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-internal-id-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_internalOrderId.limitPerMinute"
                  value={modalVelocityConfig.limits_order_internalOrderId.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-internal-id-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_internalOrderId.limitPerHour"
                  value={modalVelocityConfig.limits_order_internalOrderId.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-internal-id-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order payment amount</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_paymentAmount.limitPerSecond"
                  value={modalVelocityConfig.limits_order_paymentAmount.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-payment-amount-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_paymentAmount.limitPerMinute"
                  value={modalVelocityConfig.limits_order_paymentAmount.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-payment-amount-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_paymentAmount.limitPerHour"
                  value={modalVelocityConfig.limits_order_paymentAmount.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-payment-amount-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order billing name</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_name.limitPerSecond"
                  value={modalVelocityConfig.limits_order_billing_name.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-billing-name-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_name.limitPerMinute"
                  value={modalVelocityConfig.limits_order_billing_name.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-billing-name-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_name.limitPerHour"
                  value={modalVelocityConfig.limits_order_billing_name.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-billing-name-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order billing address</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_address.limitPerSecond"
                  value={modalVelocityConfig.limits_order_billing_address.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-billing-address-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_address.limitPerMinute"
                  value={modalVelocityConfig.limits_order_billing_address.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-billing-address-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_address.limitPerHour"
                  value={modalVelocityConfig.limits_order_billing_address.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-billing-address-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order billing items</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_items.limitPerSecond"
                  value={modalVelocityConfig.limits_order_billing_items.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-billing-items-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_items.limitPerMinute"
                  value={modalVelocityConfig.limits_order_billing_items.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-billing-items-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_billing_items.limitPerHour"
                  value={modalVelocityConfig.limits_order_billing_items.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-billing-items-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order IP address</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_ipAddress.limitPerSecond"
                  value={modalVelocityConfig.limits_order_ipAddress.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-ip-address-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_ipAddress.limitPerMinute"
                  value={modalVelocityConfig.limits_order_ipAddress.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-ip-address-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_ipAddress.limitPerHour"
                  value={modalVelocityConfig.limits_order_ipAddress.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-ip-address-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">Order checkout correlation Id</div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_checkoutCorrelationId.limitPerSecond"
                  value={modalVelocityConfig.limits_order_checkoutCorrelationId.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-order-checkout-correlation-id-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_checkoutCorrelationId.limitPerMinute"
                  value={modalVelocityConfig.limits_order_checkoutCorrelationId.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-order-checkout-correlation-id-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_order_checkoutCorrelationId.limitPerHour"
                  value={modalVelocityConfig.limits_order_checkoutCorrelationId.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-order-checkout-correlation-id-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">PayTo resolve alias</div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_resolveAlias.limitPerSecond"
                  value={modalVelocityConfig.limits_payto_resolveAlias?.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-payto-resolve-alias-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_resolveAlias.limitPerMinute"
                  value={modalVelocityConfig.limits_payto_resolveAlias?.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-payto-resolve-alias-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_resolveAlias.limitPerHour"
                  value={modalVelocityConfig.limits_payto_resolveAlias?.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-payto-resolve-alias-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">PayTo send agreement</div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_sendAgreement.limitPerSecond"
                  value={modalVelocityConfig.limits_payto_sendAgreement?.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-payto-send-agreement-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_sendAgreement.limitPerMinute"
                  value={modalVelocityConfig.limits_payto_sendAgreement?.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-payto-send-agreement-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_sendAgreement.limitPerHour"
                  value={modalVelocityConfig.limits_payto_sendAgreement?.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-payto-send-agreement-per-hour-input"
                />
              </div>
            </EditFormRow>
            <EditFormRow className="mt-2">
              <div className="col-sm-3">PayTo get agreement status</div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_getAgreementStatus.limitPerSecond"
                  value={modalVelocityConfig.limits_payto_getAgreementStatus?.limitPerSecond}
                  onChange={handleChange}
                  placeholder="Per second"
                  data-testid="limits-payto-get-agreement-status-per-second-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_getAgreementStatus.limitPerMinute"
                  value={modalVelocityConfig.limits_payto_getAgreementStatus?.limitPerMinute}
                  onChange={handleChange}
                  placeholder="Per minute"
                  data-testid="limits-payto-get-agreement-status-per-minute-input"
                />
              </div>
              <div className="col-sm-3">
                <Input
                  name="limits_payto_getAgreementStatus.limitPerHour"
                  value={modalVelocityConfig.limits_payto_getAgreementStatus?.limitPerHour}
                  onChange={handleChange}
                  placeholder="Per hour"
                  data-testid="limits-payto-get-agreement-status-per-hour-input"
                />
              </div>
            </EditFormRow>
          </LpDetails>
        </Modal>
      )}
    </LpBox>
  );
};

export default VelocityLimits;
